import {
  Alert,
  type AlertProps,
  Button,
  Stack,
  Typography
} from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import React, { useState } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { type TagsFormValues } from 'components/status-checks/edit/EditTagsStatusCheckTagsDrawer'
import { PNI_AGENT_MATCH_MESSAGES } from 'components/status-checks/edit/pni-agents-matching-tags-utils'

const MATCHING_AGENTS_VISIBLE_MAX = 10

function getMatchingAgentsAlertProps(
  tags: TagsFormValues['multiSelectTags'],
  agentsMatchingTags: string[],
  errorMessage: string | null
): {
  agentsHidden: string[]
  agentsVisible: string[]
  message: string
  type: AlertProps['severity']
} {
  const defaultValues = {
    agentsHidden: [],
    agentsVisible: [],
    type: 'info' as const
  }

  if (errorMessage) {
    return {
      ...defaultValues,
      message: errorMessage,
      type: 'error'
    }
  }

  const hasNoTags = !tags || isEmpty(tags)
  const hasNoAgents = !agentsMatchingTags.length

  if (hasNoTags && hasNoAgents) {
    return {
      ...defaultValues,
      message: PNI_AGENT_MATCH_MESSAGES.NoAgents
    }
  }

  if (hasNoAgents) {
    return {
      ...defaultValues,
      message: PNI_AGENT_MATCH_MESSAGES.NoMatches,
      type: 'warning'
    }
  }

  return {
    ...defaultValues,
    agentsHidden: agentsMatchingTags.slice(MATCHING_AGENTS_VISIBLE_MAX),
    agentsVisible:
      agentsMatchingTags.length > MATCHING_AGENTS_VISIBLE_MAX
        ? agentsMatchingTags.slice(0, MATCHING_AGENTS_VISIBLE_MAX)
        : agentsMatchingTags,
    message: PNI_AGENT_MATCH_MESSAGES.Matches
  }
}

function AgentsList({ agents }: { agents: string[] }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      {agents.map((name, i) => (
        <TruncatedTextWithTooltip
          key={name}
          sx={{ marginRight: 0.5 }}
          tooltipProps={{ title: name }}
        >
          {name}
          {i < agents.length - 1 ? ',' : ''}
        </TruncatedTextWithTooltip>
      ))}
    </Stack>
  )
}

export function PniAgentsMatchingTagsSection({
  agentsMatchingTags,
  errorMessage,
  multiSelectTags
}: {
  agentsMatchingTags: string[]
  errorMessage?: string
  multiSelectTags: TagsFormValues['multiSelectTags']
}) {
  const [isAgentsMatchingOverflowVisible, setIsAgentsMatchingOverflowVisible] =
    useState(false)

  const { agentsHidden, agentsVisible, message, type } =
    getMatchingAgentsAlertProps(
      multiSelectTags,
      agentsMatchingTags,
      errorMessage ?? null
    )

  const hasHiddenAgents = agentsHidden.length > 0

  return (
    <Alert severity={type}>
      <Typography sx={{ marginBottom: 0.5 }}>{message}</Typography>
      <AgentsList agents={agentsVisible} />
      {hasHiddenAgents && !isAgentsMatchingOverflowVisible && (
        <Button onClick={() => setIsAgentsMatchingOverflowVisible(true)}>
          Show {agentsHidden.length} more
        </Button>
      )}
      {isAgentsMatchingOverflowVisible && (
        <>
          <AgentsList agents={agentsHidden} />
          <Button onClick={() => setIsAgentsMatchingOverflowVisible(false)}>
            Hide more
          </Button>
        </>
      )}
    </Alert>
  )
}
